import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface TutorialStep {
  id: string;
  title: string;
  description: React.ReactNode;
  icon: React.ElementType;
  animation?: React.ReactNode;
}

export interface EnhancedTutorialTooltipProps {
  step: TutorialStep;
  targetRect: DOMRect | null;
  position: { 
    top: number | string; 
    left: number | string; 
    position: "top" | "bottom" | "left" | "right" | "center" 
  };
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  tooltipRef: React.RefObject<HTMLDivElement>;
}

export const EnhancedTutorialTooltip: React.FC<EnhancedTutorialTooltipProps> = ({
  step,
  targetRect,
  position,
  currentStep,
  totalSteps,
  onNext,
  onPrev,
  onClose,
  tooltipRef
}) => {
  const Icon = step.icon;

  // Förbättrad positionsberäkning baserad på targetRect och position
  const getTooltipPosition = () => {
    if (!targetRect || position.position === "center") {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    // Hämta tooltip-storleken om den finns tillgänglig
    const tooltipWidth = tooltipRef.current?.offsetWidth || 300;
    const tooltipHeight = tooltipRef.current?.offsetHeight || 200;
    
    // Padding för avstånd mellan target och tooltip
    const padding = 16;
    
    // Beräkna position baserat på targetRect och angiven position
    switch (position.position) {
      case "top":
        return {
          top: `${targetRect.top - tooltipHeight - padding}px`,
          left: `${targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2)}px`,
          transform: "none",
        };
      case "bottom":
        return {
          top: `${targetRect.bottom + padding}px`,
          left: `${targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2)}px`,
          transform: "none",
        };
      case "left":
        return {
          top: `${targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2)}px`,
          left: `${targetRect.left - tooltipWidth - padding}px`,
          transform: "none",
        };
      case "right":
        return {
          top: `${targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2)}px`,
          left: `${targetRect.right + padding}px`,
          transform: "none",
        };
      default:
        return {
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        };
    }
  };

  // Beräkna position för tooltip
  const tooltipPosition = getTooltipPosition();

  return (
    <div
      ref={tooltipRef}
      className="fixed z-[200] max-w-md bg-gray-900/95 backdrop-blur-md rounded-xl border border-gray-700 shadow-xl p-5 transition-all tutorial-animated enhanced-tutorial-tooltip"
      style={{
        top: tooltipPosition.top,
        left: tooltipPosition.left,
        transform: tooltipPosition.transform,
        maxWidth: window.innerWidth < 640 ? "90vw" : "25rem",
        // Säkerställ att tooltip alltid är överst och klickbar
        pointerEvents: "auto"
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-full bg-orange-500/20 text-orange-300">
              <Icon size={20} />
            </div>
            <h3 className="text-lg font-medium text-white">{step.title}</h3>
            <div className="ml-auto text-sm text-gray-400">
              {currentStep + 1}/{totalSteps}
            </div>
          </div>
          
          <div className="text-gray-300 step-description">
            {step.description}
          </div>
          
          {step.animation}
          
          <div className="w-full bg-gray-800 h-1 rounded-full my-4">
            <div
              className="bg-orange-400 h-1 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
            />
          </div>
          
          <div className="flex justify-between mt-4">
            {currentStep > 0 ? (
              <Button
                variant="outline"
                onClick={onPrev}
                className="text-gray-300 border-gray-700 hover:bg-gray-700 hover:text-white"
                aria-label="Föregående steg"
              >
                <ChevronLeft className="mr-2 h-4 w-4" />
                Föregående
              </Button>
            ) : (
              <div />
            )}
            
            <div className="flex gap-2">
              <Button
                variant="ghost"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-300 hover:bg-gray-700/50"
                aria-label={currentStep < totalSteps - 1 ? "Avsluta guiden" : "Stäng guiden"}
              >
                {currentStep < totalSteps - 1 ? "Avsluta" : "Stäng"}
              </Button>
              
              <Button
                onClick={onNext}
                className="bg-orange-500 hover:bg-orange-600 text-gray-900 border-orange-500/30 font-medium"
                aria-label={currentStep < totalSteps - 1 ? "Nästa steg" : "Avsluta guiden"}
              >
                {currentStep < totalSteps - 1 ? (
                  <>
                    Nästa
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </>
                ) : (
                  "Avsluta guiden"
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default EnhancedTutorialTooltip;